import {
  useFloating,
  offset,
  flip,
  shift,
  autoUpdate,
} from "@floating-ui/react";
import { useState, useEffect } from "react";
import { createPortal } from "react-dom";

type TooltipProps = {
  text: string;
  position?: "top" | "bottom" | "left" | "right";
  triggerRef: React.RefObject<SVGSVGElement>;
  id: string;
};

export const Tooltip = ({
  text,
  position = "top",
  triggerRef,
  id,
}: TooltipProps) => {
  const [open, setOpen] = useState(false);

  const { floatingStyles, refs } = useFloating({
    placement: position,
    middleware: [
      offset(8),
      flip(),
      shift({
        padding: 4,
        boundary: document.getElementById("swapper-sdk-widget")!,
      }),
    ],
    whileElementsMounted: autoUpdate,
  });

  // Connect external trigger ref to floating-ui
  useEffect(() => {
    if (triggerRef.current) {
      refs.setReference(triggerRef.current);
    }
  }, [triggerRef, refs]);

  // Show/hide on hover
  useEffect(() => {
    const triggerEl = triggerRef.current;
    if (!triggerEl) return;

    const show = () => setOpen(true);
    const hide = () => setOpen(false);

    triggerEl.addEventListener("mouseenter", show);
    triggerEl.addEventListener("mouseleave", hide);

    return () => {
      triggerEl.removeEventListener("mouseenter", show);
      triggerEl.removeEventListener("mouseleave", hide);
    };
  }, [triggerRef]);

  const tooltipRoot = document
    .getElementById("swapper-sdk-widget")
    ?.shadowRoot?.getElementById("tooltip-layer");

  if (!tooltipRoot) return null;

  return createPortal(
    <div
      ref={refs.setFloating}
      style={floatingStyles}
      id={id}
      className={`fixed z-50 border-solid border-text_primary/10 border-[1px] rounded-xl text-left shadow-lg py-2 px-4 bg-background_primary text-xs text-text_primary opacity-0 text-opacity-50 max-w-80
        transition-opacity duration-200 ease-in-out
        ${open ? "pointer-events-auto opacity-100" : "opacity-0"}`}
    >
      {text}
    </div>,
    tooltipRoot,
  );
};
